/**
 * usePersistedPeriod — SmartPeriodPill state with localStorage persistence.
 * Mirrors useExpanderState's contract: optional scope prefix (e.g. gymId)
 * so the same control on different gyms can remember separate selections.
 *
 * Storage shape: a single JSON object under "sendly-periods", indexed by
 * full key. Persisting the canonical period object means any preset the
 * user picked (weekly, last30, custom range, etc.) survives reload.
 */
import { useState, useCallback } from "react";
import { normalizePeriod } from "../utils/periodUtils";
import { makeKeyedStore, scopedKey } from "../utils";

const store = makeKeyedStore("sendly-periods");

/**
 * The stored period of a control, read without mounting it — the background
 * prefetch (docs/spec/01f-tab-loading.md) asks for the same window the control
 * will ask for when it opens. Same normalization as the hook.
 */
export function readPersistedPeriod(key, defaultValue, scope = null, allowFuture = false) {
  const fullKey = scopedKey(key, scope);
  if (!fullKey) return normalizePeriod(defaultValue, allowFuture);
  const all = store.read();
  return normalizePeriod(fullKey in all ? all[fullKey] : defaultValue, allowFuture);
}

/**
 * @param {string|null} key — Unique persist key (e.g. "leaderboard").
 *                            Null = no persistence (plain useState).
 * @param {*} defaultValue — Default period value when no stored entry exists.
 * @param {string|null} scope — Optional scope prefix (e.g. gymId). Produces
 *                              "gymId:key" storage keys.
 * @returns {[period, setPeriod]}
 */
export function usePersistedPeriod(key, defaultValue, scope = null, allowFuture = false) {
  const fullKey = scopedKey(key, scope);

  const [value, setValueRaw] = useState(() => {
    if (!fullKey) return normalizePeriod(defaultValue, allowFuture);
    const all = store.read();
    return normalizePeriod(fullKey in all ? all[fullKey] : defaultValue, allowFuture);
  });

  const setValue = useCallback((next) => {
    const resolved = typeof next === "function" ? next(value) : next;
    const norm = normalizePeriod(resolved, allowFuture);
    setValueRaw(norm);
    if (!fullKey) return;
    const all = store.read();
    all[fullKey] = norm;
    store.write(all);
  }, [fullKey, value, allowFuture]);

  return [value, setValue];
}
